// Step 8 - the overview and the per-domain record editor.
//
// This is the last screen of the wizard and the only read of the customer's
// zone the operator gets after everything has been written, so its job is to
// show what is THERE now - including what is no longer there.
//
// DELETIONS ARE THE POINT OF THE TWO NOTICES BELOW.
//
// `applyRecord` (src/lib/foreign-cf.ts) is convergent, not additive: when it
// writes a record it collapses every other record at that exact (type, FQDN)
// onto the one it wrote and DELETES the rest. On a customer's legacy zone
// carrying the classic five-host Google MX set, writing our single
// `smtp.google.com` MX in step 5 deletes the four ALT hosts. That is intended.
// What is not acceptable is the operator finding out weeks later, so:
//
//   1. A permanent honey notice names every (type, name) pair this run
//      converges and says, in each case, exactly what gets deleted there. TXT
//      is narrower than the rest - only records sharing the same singleton
//      prefix are collapsed - and the table says so, because "we deleted your
//      other TXT records" would be a false accusation.
//   2. Every domain card carries a "no longer on this zone" list, diffed
//      against the records that were on screen when this page opened. That
//      covers everything the overview itself can cause (a record editor delete,
//      a Refresh that reveals a change) and anything done in the Cloudflare
//      dashboard in parallel.
//
// The honest limit, stated so nobody assumes otherwise: step 5, 6 and 7 write
// BEFORE this screen is opened, and `live_records` is overwritten by each stage
// as it goes, so there is no stored pre-write snapshot to diff against. The
// notice is what covers those; the diff cannot.

import { useEffect, useMemo, useState } from 'react';

import {
  fdApi,
  fdErrorMessage,
  type FdDomain,
  type FdForwardingMechanism,
  type FdRecord,
  type FdRecordInput,
} from '../foreignDnsApi';
import {
  findStage,
  isZoneNotFound,
  useEscapeToClose,
  type FdStepProps,
  type FdToastKind,
} from './shared';

/** The record types the editor form can express. A zone can hold others (NS,
 *  SRV, CAA); those are listed and can be deleted, but not edited here - the
 *  route's own schema accepts only these four. */
const EDITABLE_TYPES = ['A', 'CNAME', 'TXT', 'MX'] as const;

type EditableType = (typeof EDITABLE_TYPES)[number];

const NAME_INPUT_ID = 'fd-rec-name';
const CONTENT_INPUT_ID = 'fd-rec-content';
const TTL_INPUT_ID = 'fd-rec-ttl';
const PRIORITY_INPUT_ID = 'fd-rec-priority';
const TYPE_INPUT_ID = 'fd-rec-type';

/**
 * Every (type, name) this run converges, and what convergence deletes there.
 *
 * !! MIRRORS THE BACKEND - CHANGE BOTH OR THIS SCREEN LIES !!
 *
 * Sources of truth: `basicPiRecords` in src/lib/foreign-cf.ts (steps 5),
 * `handleSingleTxt` in src/processors/foreign-dns.ts (steps 6 and 7), and
 * `SINGLETON_PREFIXES` in src/automation/panel-config/cloudflare-dns.ts, which
 * is what narrows the TXT cases from "every TXT at this name" to "every TXT at
 * this name that starts with the same prefix". The frontend cannot import any
 * of them: Dockerfile.web builds it from a stage that copies only `frontend/`.
 */
const RUN_SPECS: {
  type: EditableType;
  short: string;
  /** Set for TXT: only records whose content starts with this are collapsed. */
  prefix?: string;
  step: string;
  deletes: string;
  whenForwarding?: true;
}[] = [
  {
    type: 'MX',
    short: '@',
    step: 'Step 5',
    deletes: 'every other MX record at this name is deleted',
  },
  {
    type: 'TXT',
    short: '@',
    prefix: 'v=spf1',
    step: 'Step 5',
    deletes: 'every other TXT starting v=spf1 at this name is deleted; other TXT records are left alone',
  },
  {
    type: 'TXT',
    short: '_dmarc',
    prefix: 'v=DMARC1',
    step: 'Step 5',
    deletes: 'every other TXT starting v=DMARC1 at this name is deleted; other TXT records are left alone',
  },
  {
    type: 'CNAME',
    short: 'trk',
    step: 'Step 5',
    deletes: 'every other CNAME at this name is deleted',
  },
  {
    type: 'CNAME',
    short: 'inst',
    step: 'Step 5',
    deletes: 'every other CNAME at this name is deleted',
  },
  {
    type: 'A',
    short: '@',
    step: 'Step 5 (forwarding only)',
    deletes: 'every other A record at this name is deleted',
    whenForwarding: true,
  },
  {
    type: 'A',
    short: 'www',
    step: 'Step 5 (forwarding only)',
    deletes: 'every other A record at this name is deleted',
    whenForwarding: true,
  },
  {
    type: 'TXT',
    short: '@',
    prefix: 'google-site-verification=',
    step: 'Step 6',
    deletes:
      'every other TXT starting google-site-verification= at this name is deleted; other TXT records are left alone',
  },
  {
    type: 'TXT',
    short: 'google._domainkey',
    prefix: 'v=DKIM1',
    step: 'Step 7',
    deletes: 'every other TXT starting v=DKIM1 at this name is deleted; other TXT records are left alone',
  },
];

/** Cloudflare stores TXT content without the wrapping quotes it displays.
 *  Same helper as the backend's stripQuotes. */
function stripQuotes(value: string): string {
  return value.trim().replace(/^"|"$/g, '');
}

/** The fully-qualified name a short name resolves to ON THIS DOMAIN. `d.domain`
 *  already carries the subdomain prefix when there is one, so `@` is the domain
 *  itself and everything else hangs off it - which is exactly what
 *  recordName(short, record_prefix) + zone_root produces on the server. */
function fqdnFor(d: FdDomain, short: string): string {
  return short === '@' ? d.domain : `${short}.${d.domain}`;
}

/** Is this live record sitting at a name this run converges? Mirrors the match
 *  applyRecord makes, prefix test included and case-sensitive like the backend's. */
function writtenByRun(d: FdDomain, r: FdRecord, withForwarding: boolean): boolean {
  return RUN_SPECS.some((spec) => {
    if (spec.whenForwarding && !withForwarding) return false;
    if (spec.type !== r.type) return false;
    if (fqdnFor(d, spec.short).toLowerCase() !== r.name.toLowerCase()) return false;
    return spec.prefix === undefined || stripQuotes(r.content).startsWith(spec.prefix);
  });
}

function describeRecord(r: FdRecord): string {
  const priority = r.priority === undefined ? '' : ` (priority ${r.priority})`;
  return `${r.type} ${r.name} -> ${r.content}${priority}`;
}

function mechanismChip(mechanism: FdForwardingMechanism | null): JSX.Element {
  if (mechanism === null) return <span className="fd-cell-faint">forwarding: none recorded</span>;
  if (mechanism === 'redirect_rules') {
    return <span className="fd-chip fd-chip--yes">Redirect rules</span>;
  }
  if (mechanism === 'page_rules') return <span className="fd-chip fd-chip--warn">Page rules</span>;
  return <span className="fd-chip fd-chip--no">No forwarding</span>;
}

function fmtDate(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'never';
}

function sortRecords(records: FdRecord[]): FdRecord[] {
  return [...records].sort(
    (a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name),
  );
}

export function StepOverview({
  runId,
  detail,
  reload,
  notifyEnqueued,
  goToStep,
  toast,
}: FdStepProps): JSX.Element {
  const { run, domains, stages } = detail;
  const closed = run.status === 'completed' || run.status === 'cancelled';
  const withForwarding = run.forwarding_choice === 'yes';
  const refresh = findStage(stages, 'refresh');
  const refreshing = refresh?.status === 'running';

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [finishing, setFinishing] = useState(false);

  /**
   * The records each domain had when this page first saw it. Everything that
   * disappears from a zone while the overview is open is diffed against this,
   * so a deletion is visible on the card rather than only in Cloudflare.
   */
  const [baseline, setBaseline] = useState<Record<string, FdRecord[]>>({});
  useEffect(() => {
    setBaseline((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const d of domains) {
        if (next[d.id] === undefined && d.live_records !== null) {
          next[d.id] = d.live_records;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [domains]);

  useEscapeToClose(confirmFinish, finishing, () => setConfirmFinish(false));

  const editing = editingId === null ? null : (domains.find((d) => d.id === editingId) ?? null);

  const specRows = useMemo(
    () => RUN_SPECS.filter((s) => !s.whenForwarding || withForwarding),
    [withForwarding],
  );

  const reachable = domains.filter((d) => !isZoneNotFound(d));
  const totalRecords = domains.reduce((sum, d) => sum + (d.live_records?.length ?? 0), 0);
  /** Live records sitting at a name this run converges. Zero means no stage has
   *  written one of those names on any zone yet, so nothing has been replaced
   *  and the deletion notice has nothing to warn about. */
  const converged = domains.reduce(
    (sum, d) =>
      sum + (d.live_records ?? []).filter((r) => writtenByRun(d, r, withForwarding)).length,
    0,
  );

  async function refreshAll(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await fdApi.runStage(runId, 'refresh');
      if (res.alreadyRunning) {
        toast('warn', 'A refresh is already running - nothing new was started.');
      } else {
        notifyEnqueued();
        toast('success', `Re-reading ${reachable.length} zone(s) from Cloudflare...`);
      }
      await reload();
    } catch (err) {
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function finish(): Promise<void> {
    setFinishing(true);
    try {
      await fdApi.finishRun(runId);
      setConfirmFinish(false);
      await reload();
      toast('success', "Run finished. The customer's Cloudflare key was destroyed.");
    } catch (err) {
      toast('error', fdErrorMessage(err));
    } finally {
      setFinishing(false);
    }
  }

  function removedSince(d: FdDomain): FdRecord[] {
    const before = baseline[d.id];
    if (before === undefined || d.live_records === null) return [];
    const now = d.live_records;
    return before.filter((r) => !now.some((n) => n.id === r.id));
  }

  return (
    <>
      <section className="fd-step">
        <div className="fd-step-head">
          <h2>Step 8 - Overview</h2>
          <p>
            Everything this run wrote, read back from the customer's Cloudflare account. Use the
            per-domain editor for anything that still needs a hand, then finish the run - which is
            what destroys the borrowed key.
          </p>
        </div>
        <div className="fd-step-body">
          <div className="fd-notice">
            {domains.length} domain(s), {reachable.length} with a resolved Cloudflare zone,{' '}
            {totalRecords} record(s) currently cached. Forwarding was answered{' '}
            <strong>{run.forwarding_choice ?? 'not answered'}</strong> for this run.
          </div>

          {/* Carry-forward: deletions must be visible, not discovered later.
              Honey ONLY when this run has actually converged something. A honey
              block that is on every single time is a block the operator learns
              to skip, and then it is not warning them about anything. With
              nothing written at any of these names yet, the same content is
              true but it is background - so it renders as a plain notice. */}
          {converged > 0 ? (
            <div className="fd-notice fd-notice--warn">
              <strong>Writing a record here replaces what was at that name.</strong> Every stage in
              this wizard converges on exactly one record per name rather than adding a second one,
              so a customer's zone that carried the classic five-host Google MX set now carries only{' '}
              <span className="fd-mono">smtp.google.com</span> - the four ALT hosts were deleted when
              step 5 ran. That is intended. {converged} record(s) across this run sit at such a name
              right now, and this is the list of names it applies to.
            </div>
          ) : (
            <div className="fd-notice">
              Nothing in this run currently sits at a name the wizard converges, so no records have
              been replaced yet. When a stage does write one of the names below, it converges on a
              single record there rather than adding a second one - anything else at that name is
              deleted.
            </div>
          )}

          <div className="fd-table-wrap">
            <table className="fd-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Written by</th>
                  <th>What was removed at that name</th>
                </tr>
              </thead>
              <tbody>
                {specRows.map((spec) => (
                  <tr key={`${spec.type}-${spec.short}-${spec.prefix ?? ''}`}>
                    <td className="fd-cell-mono">{spec.type}</td>
                    <td className="fd-cell-mono">{spec.short}</td>
                    <td className="fd-cell-faint">{spec.step}</td>
                    <td className="fd-cell-faint">{spec.deletes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <span className="fd-hint">
            Names are shown relative to each domain: <span className="fd-mono">@</span> is the domain
            itself, so on <span className="fd-mono">mail.acme.com</span> the MX above sits on{' '}
            <span className="fd-mono">mail.acme.com</span> and <span className="fd-mono">trk</span>{' '}
            on <span className="fd-mono">trk.mail.acme.com</span>. Records at these names are marked{' '}
            <span className="fd-chip">this run</span> in the tables below.
          </span>

          {error && <div className="fd-notice fd-notice--err">{error}</div>}

          {refreshing && (
            <div className="fd-notice fd-notice--info">
              Re-reading every zone from Cloudflare. This page updates itself.
            </div>
          )}

          {!refreshing && refresh?.status === 'failed' && (
            <div className="fd-notice fd-notice--err">
              The last refresh failed: {refresh.error_message ?? 'no message recorded'}
            </div>
          )}

          <div className="fd-actions">
            <button
              type="button"
              className="fd-btn fd-btn--primary"
              onClick={() => void refreshAll()}
              disabled={busy || refreshing || closed || reachable.length === 0}
            >
              {refreshing ? 'Refreshing...' : 'Refresh from Cloudflare'}
            </button>
            <button
              type="button"
              className="fd-btn fd-btn--secondary"
              onClick={() => void goToStep(7)}
              disabled={busy}
            >
              Back to DKIM
            </button>
          </div>

          {closed && (
            <div className="fd-notice">
              This run is {run.status} and its Cloudflare key was destroyed, so nothing here can
              read or write the customer's zone any more. What is shown is the last state we read.
            </div>
          )}
        </div>
      </section>

      {domains.map((d) => {
        const records = d.live_records;
        const removed = removedSince(d);
        return (
          <section className="fd-step" key={d.id}>
            <div className="fd-step-head">
              <h2 className="fd-mono">{d.domain}</h2>
              <div className="fd-actions">
                <span className={`fd-badge fd-badge--${d.basic_status}`}>
                  basic {d.basic_status}
                </span>
                <span className={`fd-badge fd-badge--${d.verification_status}`}>
                  verify {d.verification_status}
                </span>
                <span className={`fd-badge fd-badge--${d.dkim_status}`}>dkim {d.dkim_status}</span>
                <button
                  type="button"
                  className="fd-btn fd-btn--secondary fd-btn--small"
                  onClick={() => setEditingId(d.id)}
                  disabled={closed || isZoneNotFound(d)}
                  title={
                    isZoneNotFound(d)
                      ? 'This domain has no Cloudflare zone - re-run the domain probe on step 2'
                      : closed
                        ? 'This run is closed and its key was destroyed'
                        : 'Add, edit or delete records on this zone'
                  }
                >
                  Edit records
                </button>
              </div>
              <p>
                Zone <span className="fd-mono">{d.zone_root}</span>
                {d.record_prefix === null ? '' : ` (records written under ${d.record_prefix})`} -{' '}
                {isZoneNotFound(d) ? 'no zone resolved' : (d.zone_status ?? 'unknown status')} -{' '}
                {records === null ? 'no records read yet' : `${records.length} record(s)`}, last read{' '}
                {fmtDate(d.live_records_at)}.
              </p>
            </div>

            <div className="fd-step-body">
              <div className="fd-actions">
                {mechanismChip(d.forwarding_mechanism)}
                {d.forwarding_url !== null && (
                  <span className="fd-cell-faint fd-mono">{d.forwarding_url}</span>
                )}
              </div>

              {removed.length > 0 && (
                <div className="fd-notice fd-notice--warn">
                  <strong>
                    {removed.length} record(s) are no longer on this zone since this page opened:
                  </strong>{' '}
                  <span className="fd-mono">{removed.map(describeRecord).join('; ')}</span>
                </div>
              )}

              {d.last_error !== null && (
                <span className="fd-hint">
                  Last message on this domain: <span className="fd-mono">{d.last_error}</span>. It
                  belongs to whichever step touched the domain last and is not always a failure -
                  the badges above are the verdicts.
                </span>
              )}
            </div>

            {records !== null && records.length > 0 ? (
              <div className="fd-table-wrap">
                <table className="fd-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Content</th>
                      <th className="fd-col-center">TTL</th>
                      <th className="fd-col-center">Prio</th>
                      <th className="fd-col-center">Proxied</th>
                      <th className="fd-col-center">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortRecords(records).map((r) => (
                      <tr key={r.id}>
                        <td className="fd-cell-mono">{r.type}</td>
                        <td className="fd-cell-mono">{r.name}</td>
                        <td className="fd-cell-mono">{r.content}</td>
                        <td className="fd-col-center fd-cell-faint">{r.ttl}</td>
                        <td className="fd-col-center fd-cell-faint">{r.priority ?? '-'}</td>
                        <td className="fd-col-center fd-cell-faint">
                          {r.proxied === undefined ? '-' : r.proxied ? 'yes' : 'no'}
                        </td>
                        <td className="fd-col-center">
                          {writtenByRun(d, r, withForwarding) ? (
                            <span className="fd-chip">this run</span>
                          ) : (
                            <span className="fd-cell-faint">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="fd-empty">
                {isZoneNotFound(d)
                  ? 'No Cloudflare zone was resolved for this domain, so nothing was written and nothing can be read.'
                  : 'No records have been read for this domain yet. Use Refresh from Cloudflare.'}
              </div>
            )}
          </section>
        );
      })}

      <section className="fd-step">
        <div className="fd-step-head">
          <h2>Finish the run</h2>
          <p>
            Finishing is what destroys the customer's borrowed Cloudflare key. Everything typed into
            this run is kept as the record of what was done; only the key goes.
          </p>
        </div>
        <div className="fd-step-body">
          {closed ? (
            <div className="fd-notice">
              This run is already {run.status}. Its key was destroyed and it will not accept a new
              one.
            </div>
          ) : (
            <div className="fd-actions">
              <button
                type="button"
                className="fd-btn fd-btn--primary"
                onClick={() => setConfirmFinish(true)}
                disabled={busy || refreshing}
              >
                Finish run
              </button>
            </div>
          )}
        </div>
      </section>

      {editing !== null && (
        <RecordEditor
          runId={runId}
          domain={editing}
          toast={toast}
          onClose={() => {
            setEditingId(null);
            void reload();
          }}
        />
      )}

      {confirmFinish && (
        <div className="fd-modal-overlay">
          <div className="fd-modal">
            <div className="fd-modal-header">
              <h3 className="fd-modal-title">Finish this run?</h3>
            </div>
            <div className="fd-modal-body">
              <p>This clears the customer's Cloudflare key from our database and closes the run.</p>
              <p>
                Nothing already written to their DNS is undone, and every domain, verification code
                and DKIM value stays on the run. A finished run will not accept a key again - start
                a new run to configure more domains.
              </p>
            </div>
            <div className="fd-modal-footer">
              <button
                type="button"
                className="fd-btn fd-btn--primary"
                onClick={() => void finish()}
                disabled={finishing}
              >
                {finishing ? 'Finishing...' : 'Finish run and destroy the key'}
              </button>
              <button
                type="button"
                className="fd-btn fd-btn--secondary"
                onClick={() => setConfirmFinish(false)}
                disabled={finishing}
              >
                Keep the run open
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Per-domain record editor ────────────────────────────────────────────── */

interface FormState {
  type: EditableType;
  name: string;
  content: string;
  ttl: string;
  priority: string;
  proxied: boolean;
}

function blankForm(d: FdDomain): FormState {
  return {
    type: 'TXT',
    // Names are relative to the ZONE, not to the domain row: on `mail.acme.com`
    // in zone `acme.com`, the domain's own apex is the name `mail`.
    name: d.record_prefix ?? '@',
    content: '',
    ttl: '3600',
    priority: '10',
    proxied: false,
  };
}

function parseTtl(raw: string): number | null {
  const n = Number.parseInt(raw.trim(), 10);
  if (!Number.isFinite(n) || String(n) !== raw.trim()) return null;
  return n >= 1 && n <= 86_400 ? n : null;
}

function parsePriority(raw: string): number | null {
  const n = Number.parseInt(raw.trim(), 10);
  if (!Number.isFinite(n) || String(n) !== raw.trim()) return null;
  return n >= 0 && n <= 65_535 ? n : null;
}

/**
 * The one place in this feature that writes a single record straight to the
 * customer's zone.
 *
 * Two rules the form enforces so the operator is not surprised by a server
 * coercion (routes/foreign-dns-runs.ts, toRecordBody):
 *   - a CNAME is ALWAYS unproxied. There is no toggle; there is fixed text.
 *     Orange-clouding trk/inst breaks the tracking hosts and the stage
 *     read-backs compare `proxied`, so a proxied CNAME fails the run forever.
 *   - a proxied record must be TTL 1. Cloudflare rejects anything else, so the
 *     field is pinned to 1 and disabled the moment Proxied is ticked.
 *
 * Every mutation replaces the list from the SERVER's fresh read-back, never
 * from the submitted body: the route re-lists the zone after each write, and a
 * 200 from a write is not proof of what the zone holds.
 */
function RecordEditor({
  runId,
  domain,
  toast,
  onClose,
}: {
  runId: string;
  domain: FdDomain;
  toast: (type: FdToastKind, message: string) => void;
  onClose: () => void;
}): JSX.Element {
  const [records, setRecords] = useState<FdRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastChange, setLastChange] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(() => blankForm(domain));
  const [editingRecord, setEditingRecord] = useState<FdRecord | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FdRecord | null>(null);

  // The confirm sits on top of the editor, so Escape closes the confirm first
  // and only reaches the editor once there is no confirm to dismiss.
  useEscapeToClose(confirmDelete === null, busy, onClose);
  useEscapeToClose(confirmDelete !== null, busy, () => setConfirmDelete(null));

  useEffect(() => {
    let live = true;
    setLoading(true);
    fdApi
      .listRecords(runId, domain.id)
      .then((fresh) => {
        if (live) setRecords(fresh);
      })
      .catch((err: unknown) => {
        if (live) setError(fdErrorMessage(err));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [runId, domain.id]);

  const proxiedNow = form.type === 'A' ? form.proxied : form.type === 'CNAME' ? false : undefined;
  const ttlLocked = proxiedNow === true;
  const ttlValue = parseTtl(form.ttl);
  const priorityValue = parsePriority(form.priority);
  const nameOk = form.name.trim() !== '';
  const contentOk = form.content.trim() !== '';
  const ttlOk = ttlLocked || ttlValue !== null;
  const priorityOk = form.type !== 'MX' || priorityValue !== null;
  const canSubmit = nameOk && contentOk && ttlOk && priorityOk && !busy;

  function buildInput(): FdRecordInput {
    const input: FdRecordInput = {
      type: form.type,
      name: form.name.trim(),
      content: form.content.trim(),
      ttl: ttlLocked ? 1 : (ttlValue ?? 3600),
    };
    if (proxiedNow !== undefined) input.proxied = proxiedNow;
    if (form.type === 'MX' && priorityValue !== null) input.priority = priorityValue;
    return input;
  }

  /** Report what the zone lost as well as what it gained. The editor's own
   *  writes are additive (the route calls createRecord, not applyRecord), so a
   *  removal here should be rare - which is exactly why it is worth naming when
   *  it does happen. */
  function applyResult(next: FdRecord[], verb: string, subject: FdRecord): void {
    const before = records ?? [];
    const removed = before.filter((r) => !next.some((n) => n.id === r.id));
    setRecords(next);
    setLastChange(
      `${verb} ${describeRecord(subject)}.` +
        (removed.length === 0
          ? ''
          : ` ${removed.length} record(s) are no longer on the zone: ${removed
              .map(describeRecord)
              .join('; ')}`),
    );
  }

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const input = buildInput();
      const res = editingRecord
        ? await fdApi.editRecord(runId, domain.id, editingRecord.id, input)
        : await fdApi.addRecord(runId, domain.id, input);
      applyResult(res.records, editingRecord ? 'Updated' : 'Added', res.record);
      setEditingRecord(null);
      setForm(blankForm(domain));
      toast('success', `${editingRecord ? 'Updated' : 'Added'} ${res.record.type} ${res.record.name}`);
    } catch (err) {
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(record: FdRecord): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const next = await fdApi.deleteRecord(runId, domain.id, record.id);
      applyResult(next, 'Deleted', record);
      setConfirmDelete(null);
      if (editingRecord?.id === record.id) {
        setEditingRecord(null);
        setForm(blankForm(domain));
      }
      toast('success', `Deleted ${record.type} ${record.name}`);
    } catch (err) {
      setError(fdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(record: FdRecord): void {
    setEditingRecord(record);
    setLastChange(null);
    setForm({
      type: record.type as EditableType,
      name: record.name,
      content: record.content,
      ttl: String(record.ttl),
      priority: String(record.priority ?? 10),
      proxied: record.proxied ?? false,
    });
  }

  return (
    <>
      <div className="fd-modal-overlay">
        <div className="fd-modal">
          <div className="fd-modal-header">
            <h3 className="fd-modal-title">
              Records on <span className="fd-mono">{domain.domain}</span>
            </h3>
            <button
              type="button"
              className="fd-btn fd-btn--ghost fd-btn--small"
              onClick={onClose}
              disabled={busy}
            >
              Close
            </button>
          </div>

          <div className="fd-modal-body">
            {/* The asymmetry with the wizard's own steps is deliberate - an
                editor that converged would let one mistyped name silently
                delete a customer's records - but it has to be SAID, because the
                overview one click away opens by explaining that writing a
                record replaces what was at that name. */}
            <p className="fd-hint">
              These writes go straight to the customer's Cloudflare zone{' '}
              <span className="fd-mono">{domain.zone_root}</span>. Names are relative to that zone:
              use <span className="fd-mono">@</span> for its apex
              {domain.record_prefix === null
                ? '.'
                : `, and ${domain.record_prefix} for this domain's own apex.`}{' '}
              The list below is re-read from Cloudflare after every change.{' '}
              <strong>
                A record added here is ADDED, not replaced - unlike the wizard's own steps, this
                form never converges on one record per name.
              </strong>{' '}
              Adding a second MX at a name that already has one leaves both in place and splits mail
              delivery between them, so delete what you are replacing first.
            </p>

            {error && <div className="fd-notice fd-notice--err">{error}</div>}
            {lastChange && <div className="fd-notice fd-notice--ok">{lastChange}</div>}

            {loading ? (
              <div className="fd-empty">Reading the zone from Cloudflare...</div>
            ) : records === null ? (
              <div className="fd-empty">The zone could not be read.</div>
            ) : (
              <div className="fd-table-wrap">
                <table className="fd-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Content</th>
                      <th className="fd-col-center">TTL</th>
                      <th className="fd-col-center" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortRecords(records).map((r) => {
                      const editable = (EDITABLE_TYPES as readonly string[]).includes(r.type);
                      return (
                        <tr key={r.id}>
                          <td className="fd-cell-mono">{r.type}</td>
                          <td className="fd-cell-mono">{r.name}</td>
                          <td className="fd-cell-mono">{r.content}</td>
                          <td className="fd-col-center fd-cell-faint">{r.ttl}</td>
                          <td className="fd-col-center">
                            <button
                              type="button"
                              className="fd-btn fd-btn--ghost fd-btn--small"
                              onClick={() => startEdit(r)}
                              disabled={busy || !editable}
                              title={
                                editable
                                  ? 'Load this record into the form'
                                  : `${r.type} records cannot be edited here - delete and re-create it in Cloudflare`
                              }
                            >
                              Edit
                            </button>{' '}
                            <button
                              type="button"
                              className="fd-btn fd-btn--danger fd-btn--small"
                              onClick={() => setConfirmDelete(r)}
                              disabled={busy}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="fd-field">
              <label className="fd-field-label" htmlFor={TYPE_INPUT_ID}>
                {editingRecord ? 'Editing an existing record' : 'Add a record'} - type
              </label>
              <select
                id={TYPE_INPUT_ID}
                className="fd-input"
                value={form.type}
                disabled={busy}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value as EditableType }))
                }
              >
                {EDITABLE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="fd-field">
              <label className="fd-field-label" htmlFor={NAME_INPUT_ID}>
                Name
              </label>
              <input
                id={NAME_INPUT_ID}
                className="fd-input fd-mono"
                type="text"
                value={form.name}
                spellCheck={false}
                autoComplete="off"
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>

            <div className="fd-field">
              <label className="fd-field-label" htmlFor={CONTENT_INPUT_ID}>
                Content
              </label>
              <input
                id={CONTENT_INPUT_ID}
                className="fd-input fd-mono"
                type="text"
                value={form.content}
                spellCheck={false}
                autoComplete="off"
                disabled={busy}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              />
            </div>

            <div className="fd-field">
              <label className="fd-field-label" htmlFor={TTL_INPUT_ID}>
                TTL in seconds
              </label>
              <input
                id={TTL_INPUT_ID}
                className="fd-input fd-mono"
                type="text"
                value={ttlLocked ? '1' : form.ttl}
                inputMode="numeric"
                disabled={busy || ttlLocked}
                onChange={(e) => setForm((f) => ({ ...f, ttl: e.target.value }))}
              />
              <span className="fd-hint">
                {ttlLocked
                  ? 'A proxied record must use TTL 1 (Cloudflare automatic). Cloudflare rejects anything else, so this is pinned.'
                  : '1 to 86400. 1 means Cloudflare automatic.'}
                {!ttlLocked && ttlValue === null && ' This value is not a whole number in that range.'}
              </span>
            </div>

            {form.type === 'MX' && (
              <div className="fd-field">
                <label className="fd-field-label" htmlFor={PRIORITY_INPUT_ID}>
                  Priority
                </label>
                <input
                  id={PRIORITY_INPUT_ID}
                  className="fd-input fd-mono"
                  type="text"
                  value={form.priority}
                  inputMode="numeric"
                  disabled={busy}
                  onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                />
                <span className="fd-hint">
                  0 to 65535. The Basic PI DNS step writes priority 1.
                  {priorityValue === null && ' This value is not a whole number in that range.'}
                </span>
              </div>
            )}

            {form.type === 'A' && (
              <div className="fd-field">
                <span className="fd-field-label">Cloudflare proxy</span>
                <label className="fd-checkline" htmlFor="fd-rec-proxied">
                  <input
                    id="fd-rec-proxied"
                    className="fd-check"
                    type="checkbox"
                    checked={form.proxied}
                    disabled={busy}
                    onChange={(e) => setForm((f) => ({ ...f, proxied: e.target.checked }))}
                  />
                  Proxied (orange cloud)
                </label>
              </div>
            )}

            {form.type === 'CNAME' && (
              <div className="fd-field">
                <span className="fd-field-label">Cloudflare proxy</span>
                <span className="fd-hint">DNS only (never proxied)</span>
              </div>
            )}
          </div>

          <div className="fd-modal-footer">
            <button
              type="button"
              className="fd-btn fd-btn--primary"
              onClick={() => void submit()}
              disabled={!canSubmit}
            >
              {busy
                ? 'Writing...'
                : editingRecord
                  ? 'Save this record'
                  : 'Add record to the zone'}
            </button>
            {editingRecord && (
              <button
                type="button"
                className="fd-btn fd-btn--secondary"
                onClick={() => {
                  setEditingRecord(null);
                  setForm(blankForm(domain));
                }}
                disabled={busy}
              >
                Cancel edit
              </button>
            )}
            <div className="fd-actions-spacer" />
            <button
              type="button"
              className="fd-btn fd-btn--secondary"
              onClick={onClose}
              disabled={busy}
            >
              Done
            </button>
          </div>
        </div>
      </div>

      {confirmDelete && (
        <div className="fd-modal-overlay">
          <div className="fd-modal">
            <div className="fd-modal-header">
              <h3 className="fd-modal-title">Delete this record?</h3>
            </div>
            <div className="fd-modal-body">
              <p>
                <span className="fd-mono">{describeRecord(confirmDelete)}</span>
              </p>
              <p>
                It is deleted from the customer's own Cloudflare zone{' '}
                <span className="fd-mono">{domain.zone_root}</span> and cannot be undone from here.
              </p>
            </div>
            <div className="fd-modal-footer">
              <button
                type="button"
                className="fd-btn fd-btn--danger"
                onClick={() => void remove(confirmDelete)}
                disabled={busy}
              >
                {busy ? 'Deleting...' : 'Delete the record'}
              </button>
              <button
                type="button"
                className="fd-btn fd-btn--secondary"
                onClick={() => setConfirmDelete(null)}
                disabled={busy}
              >
                Keep it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
